import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CashoutAsset, SelectedAsset, CashoutState, SubmitCashoutResult } from '../../types/cashout.types';
import { fetchAvailableAssets, submitCashoutRequest } from './cashoutThunks';

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: CashoutState = {
  availableAssets: [],
  selectedAssets: [],
  totalEstimatedCashOut: 0,
  loading: false,
  error: null,
  submitError: null as SubmitCashoutResult | null,
  submitDisabled: false,
  metadata: null,
};

/**
 * Recalculate total cash out amount from selected assets (quantity * rate)
 */
const recalculateTotalCashOut = (selectedAssets: SelectedAsset[]): number =>
  selectedAssets.reduce((sum, asset) => sum + (asset.selectedCashOutQuantity * asset.rate), 0);

/**
 * Convert amount (USD) to quantity based on asset rate
 */
const amountToQuantity = (amount: number, rate: number): number => {
  return rate > 0 ? amount / rate : 0;
};

/**
 * Clear all error-related fields
 */
const resetErrorStates = (state: CashoutState) => {
  state.error = null;
  state.submitError = null;
};

/**
 * Clear selections and reset submission state
 */
const resetSelectionsState = (state: CashoutState) => {
  state.selectedAssets = [];
  state.totalEstimatedCashOut = 0;
  state.submitError = null;
  state.submitDisabled = false;
};

/**
 * Reset submission button state (unlock for retry)
 */
const resetSubmitState = (state: CashoutState) => {
  state.submitDisabled = false;
  state.submitError = null;
};

/**
 * Create a new selected asset from available asset
 * Initializes slider at 100% and sets quantity to eligible cashout quantity
 */
const createSelectedAsset = (asset: CashoutAsset): SelectedAsset => ({
  ...asset,
  slider: 100,
  selectedCashOutQuantity: asset.eligibleCashOutQuantity,
});

/**
 * Complete data wipe (for logout/reset scenarios)
 */
const resetCompleteState = (state: CashoutState) => {
  state.availableAssets = [];
  state.selectedAssets = [];
  state.totalEstimatedCashOut = 0;
  state.loading = false;
  state.error = null;
  state.submitError = null;
  state.submitDisabled = false;
  state.metadata = null;
};

const cashoutSlice = createSlice({
  name: 'cashout',
  initialState,
  reducers: {
    /**
     * Toggle asset selection – add or remove from selected list
     */
    toggleAssetSelection: (state, action: PayloadAction<CashoutAsset>) => {
      const asset = action.payload;
      const existingIndex = state.selectedAssets.findIndex((a) => a.id === asset.id);

      if (existingIndex >= 0) {
        state.selectedAssets.splice(existingIndex, 1);
      } else {
        state.selectedAssets.push(createSelectedAsset(asset));
      }

      state.totalEstimatedCashOut = recalculateTotalCashOut(state.selectedAssets);
      resetSubmitState(state);
    },

    /**
     * Update asset quantity via slider (percent-based 0-100)
     */
    updateAssetSlider: (
      state,
      action: PayloadAction<{ assetId: string; sliderValue: number }>
    ) => {
      const { assetId, sliderValue } = action.payload;
      const asset = state.selectedAssets.find((a) => a.id === assetId);

      if (asset) {
        asset.slider = sliderValue;
        asset.selectedCashOutQuantity = (asset.eligibleCashOutQuantity * sliderValue) / 100;
        state.totalEstimatedCashOut = recalculateTotalCashOut(state.selectedAssets);
        resetSubmitState(state);
      }
    },

    /**
     * Update asset via amount input (user enters USD, converted to quantity)
     */
    updateAssetByAmount: (
      state,
      action: PayloadAction<{ assetId: string; amountUSD: number }>
    ) => {
      const { assetId, amountUSD } = action.payload;
      const asset = state.selectedAssets.find((a) => a.id === assetId);

      if (asset) {
        // Convert USD amount to quantity
        const quantity = amountToQuantity(amountUSD, asset.rate);

        // Clamp quantity between min and max
        const minQuantity = asset.minimumCashOutQuantity ?? 0;
        const maxQuantity = asset.eligibleCashOutQuantity;
        const clampedQuantity = Math.min(Math.max(quantity, minQuantity), maxQuantity);

        asset.selectedCashOutQuantity = clampedQuantity;
        asset.slider = maxQuantity > 0 ? (clampedQuantity / maxQuantity) * 100 : 0;

        state.totalEstimatedCashOut = recalculateTotalCashOut(state.selectedAssets);
        resetSubmitState(state);
      }
    },

    /**
     * DEPRECATED: Use updateAssetByAmount instead
     * Kept for backward compatibility
     */
    updateAssetAmount: (
      state,
      action: PayloadAction<{ assetId: string; amount: number }>
    ) => {
      const { assetId, amount } = action.payload;
      const asset = state.selectedAssets.find((a) => a.id === assetId);

      if (asset) {
        const minQuantity = asset.minimumCashOutQuantity ?? 0;
        const maxQuantity = asset.eligibleCashOutQuantity;
        const clampedQuantity = Math.min(Math.max(amount, minQuantity), maxQuantity);

        asset.selectedCashOutQuantity = clampedQuantity;
        asset.slider = maxQuantity > 0 ? (clampedQuantity / maxQuantity) * 100 : 0;

        state.totalEstimatedCashOut = recalculateTotalCashOut(state.selectedAssets);
        resetSubmitState(state);
      }
    },

    /**
     * Clear fetch-related errors only (not submission errors)
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Clear submission errors and unlock submit button for retry
     */
    clearSubmitError: (state) => {
      state.submitError = null;
      state.submitDisabled = false;
    },

    /**
     * Clear all errors at once
     */
    clearAllErrors: (state) => {
      resetErrorStates(state);
    },

    /**
     * Clear selections and reset to allow new submission
     */
    clearAllSelections: (state) => {
      resetSelectionsState(state);
    },

    /**
     * Remove successfully processed assets from selection (partial success cleanup)
     */
    partialSuccessCleanup: (state, action: PayloadAction<string[]>) => {
      const succeededIds = new Set(action.payload);
      state.selectedAssets = state.selectedAssets.filter(a => !succeededIds.has(a.id));
      state.totalEstimatedCashOut = recalculateTotalCashOut(state.selectedAssets);
    },

    /**
     * Complete state reset (for logout or account switch)
     */
    resetCashoutState: (state) => {
      resetCompleteState(state);
    },
  },

  extraReducers: (builder) => {
    // ── Fetch Available Assets ──────────────────────────────────────────────

    builder.addCase(fetchAvailableAssets.pending, (state) => {
      state.loading = true;
      state.error = null;
    });

    builder.addCase(fetchAvailableAssets.fulfilled, (state, action) => {
      state.loading = false;
      state.availableAssets = action.payload.data;
      state.metadata = action.payload.metadata;
      state.error = null;
    });

    builder.addCase(fetchAvailableAssets.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
      state.availableAssets = [];
      state.metadata = null;
      resetSelectionsState(state);
    });

    // ── Submit Cashout Request ──────────────────────────────────────────────
    builder.addCase(submitCashoutRequest.pending, (state) => {
      state.loading = true;
      state.submitError = null;
    });

    builder.addCase(submitCashoutRequest.fulfilled, (state) => {
      state.loading = false;
      resetErrorStates(state);
      resetSelectionsState(state);
    });

    builder.addCase(submitCashoutRequest.rejected, (state, action) => {
      state.loading = false;
      state.submitError = action.payload as SubmitCashoutResult;
      state.submitDisabled = true;
    });
  },
});

export const {
  toggleAssetSelection,
  updateAssetSlider,
  updateAssetByAmount,
  updateAssetAmount,
  clearAllSelections,
  clearError,
  clearSubmitError,
  clearAllErrors,
  partialSuccessCleanup,
  resetCashoutState,
} = cashoutSlice.actions;

export default cashoutSlice.reducer;